<template>
  <v-container class="fill-height" fluid pa-0 ma-0>
    <l-map
      :zoom.sync="mapZoom"
      :minZoom="minZoom"
      :center="center"
      :options="mapOptions"
      style="z-index: 0;"
    >
      <l-tile-layer :url="url" :attribution="attribution"> </l-tile-layer>

      <div v-for="(layer, i) in layers" :key="i">
        <l-geo-json
          v-if="layer.show"
          :geojson="layer.geoJson"
          :options="layer.options"
        ></l-geo-json>
      </div>
    </l-map>
  </v-container>
</template>

<script>
import L from "leaflet";
import { LMap, LTileLayer, LGeoJson } from "vue2-leaflet";
import { MapLayer, MapLayerGroup } from "../models/MapLayer";

export default {
  name: "Map",
  components: {
    LMap,
    LTileLayer,
    LGeoJson
  },
  props: {
    layerGroups: {
      type: Array
    },
    zoom: {
      type: Number,
      default: 6
    },
    minZoom: {
      type: Number,
      default: 7
    }
  },
  data: () => ({
    enableTooltip: true,
    mapZoom: 6,
    center: [2.713, -60.601],
    url: "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
    attribution:
      '&copy; <a href="http://osm.org/copyright">OpenStreetMap</a> contributors',
    message: false,
    hints: true,
    /** @type {L.MapOptions} */
    mapOptions: {
      touchZoom: true,
      zoomControl: false,
      attributionControl: true
    }
  }),
  computed: {
    /** @return {MapLayerGroup[]} */
    mapLayerGroups() {
      return (
        this.layerGroups || [
          new MapLayerGroup(
            "Autorização de queimadas",
            { color: "yellow" },
            {
              2019: new MapLayer("queima.json", this.queimadaOptions)
            }
          ),
          new MapLayerGroup(
            "Autorização de desmatamento",
            { color: "orange" },
            {
              2019: new MapLayer("desmatamento.json", this.desmatamentoOptions)
            }
          ),
          new MapLayerGroup(
            "Embargos",
            { color: "red" },
            {
              2015: new MapLayer("embargos2015.json", this.embargosOption),
              2016: new MapLayer("embargos2016.json", this.embargosOption),
              2017: new MapLayer("embargos2017.json", this.embargosOption),
              2018: new MapLayer("embargos2018.json", this.embargosOption),
              2019: new MapLayer("embargos2019.json", this.embargosOption)
            }
          )
        ]
      );
    },

    /** @returns {MapLayer[]} */
    layers() {
      /** @type {MapLayer[]} */
      var layers = [];

      for (const i in this.mapLayerGroups) {
        if (Object.prototype.hasOwnProperty.call(this.mapLayerGroups, i)) {
          const group = this.mapLayerGroups[i];
          for (const j in group.layers) {
            if (Object.prototype.hasOwnProperty.call(group.layers, j)) {
              const layer = group.layers[j];
              layers.push(layer);
            }
          }
        }
      }

      return layers;
    },

    /**
     * @returns {L.GeoJSONOptions}
     */
    desmatamentoOptions() {
      return {
        onEachFeature: (feature, layer) => {
          var popupContent =
            "<h3>Número da autorização </h3>" +
            feature.properties.NUMEROAUT +
            "<br>" +
            "<h3>Vigência </h3>" +
            feature.properties.VALIDADE;

          if (feature.properties && feature.properties.popupContent) {
            popupContent += feature.properties.popupContent;
          }

          layer.bindPopup(popupContent);
        },
        style: {
          weight: 2,
          color: "#ef6c00",
          opacity: 1,
          fillColor: "#ef6c00",
          fillOpacity: 0.6
        }
      };
    },

    /**
     * @returns {L.GeoJSONOptions}
     */
    queimadaOptions() {
      var self = this;
      return {
        onEachFeature: (feature, layer) => {
          var popupContent =
            "<h3>Nome do autorizado</h3>" +
            feature.properties.NOME +
            "<h3>Vigência</h3>" +
            feature.properties.vigencia;

          if (feature.properties && feature.properties.popupContent) {
            popupContent += feature.properties.popupContent;
          }

          layer.bindPopup(popupContent);
        },
        pointToLayer: function(feature, latlng) {
          var inputDate = feature.properties.vigencia;

          var dates = inputDate.split("a").map(function(x) {
            return self.$moment(x, "DD/MM/YYYY");
          });
          var today = self.$moment({ hour: 0, minute: 0, seconds: 0 });
          var isBetweenDates = dates[0] <= today && today <= dates[1];

          var fillColor = "#ffd966";

          if (isBetweenDates) {
            fillColor = "#00ff00";
          }

          return L.circleMarker(latlng, {
            radius: 5,
            fillColor: fillColor,
            color: "#fff",
            weight: 1,
            opacity: 0.5,
            fillOpacity: 0.5
          });
        }
      };
    },

    /**
     * @returns {L.GeoJSONOptions}
     */
    embargosOption() {
      return {
        onEachFeature: (feature, layer) => {
          var popupContent =
            "<h3>Infração </h3>" +
            feature.properties.CRIME +
            "<h3>Data da infração</h3>" +
            feature.properties.DATA_AU;

          if (feature.properties && feature.properties.popupContent) {
            popupContent += feature.properties.popupContent;
          }

          layer.bindPopup(popupContent);
        },
        pointToLayer: function(feature, latlng) {
          return L.circleMarker(latlng, {
            radius: 3,
            fillColor: "#e62e00",
            color: "#fff",
            weight: 1,
            opacity: 0.2,
            fillOpacity: 0.5
          });
        }
      };
    },

    onEachFeatureFunction() {
      if (!this.enableTooltip) {
        return () => {};
      }
      return (feature, layer) => {
        var popupContent =
          ("Idetificação: " + feature.properties.field_3 ||
            feature.properties.CRIME ||
            feature.properties.NOME ||
            feature.properties.NUMEROAUT) +
          "  -  Data: " +
          (feature.properties.vigencia ||
            feature.properties.NUMEROAUT ||
            feature.properties.sigla ||
            feature.properties.DATA_AU ||
            feature.properties.VALIDADE);

        if (feature.properties && feature.properties.popupContent) {
          popupContent += feature.properties.popupContent;
        }

        layer.bindPopup(popupContent);
      };
    }
  },

  methods: {
    outputDates() {
      var self = this;
      return this.inputDate.split(" a ").map(function(x) {
        return self.$moment(x, "DD/MM/YYYY");
      });
    },
    isBetween() {
      var today = this.$moment({ hour: 0, minute: 0, seconds: 0 });
      return this.outputDates[0] <= today && today <= this.outputDates[1];
    }
  },

  watch: {
    zoom(value) {
      this.mapZoom = value;
    },
    mapZoom(value) {
      this.$emit("update:zoom", value);
    },
    mapLayerGroups(value) {
      this.$emit("update:layer-groups", value);
    }
  },

  async created() {
    var self = this;
    this.$emit("update:loading", true);

    /** @type {Promise[]} */
    var promises = [];

    this.layers.forEach(layer => {
      promises.push(layer.fetch());
    });

    Promise.all(promises).then(function() {
      self.$emit("update:loading", false);
    });
  },

  mounted() {
    this.$emit("update:layer-groups", this.mapLayerGroups);
  }
};
</script>

<style>
</style>